import * as Sentry from '@sentry/react';
import { useState, type ReactElement } from 'react';

import { getErrorMessage } from '@/hooks/api/errorHelpers';
import {
  useBitbucketConnect,
  useBitbucketDisconnect,
  useBitbucketStatus,
  useBitbucketSync,
  useBitbucketSyncStatus,
  useBitbucketUpdateToken,
} from '@/hooks/api/useBitbucket';
import { useToast } from '@/hooks/use-toast';
import type {
  GrantedIntegrationAccess,
  RenderableIntegrationAccess,
} from '@/hooks/useIntegrationAccess';
import { BitbucketConnectDialog } from '@/pages/organization/components/BitbucketConnectDialog';
import { BitbucketUpdateTokenDialog } from '@/pages/organization/components/BitbucketUpdateTokenDialog';
import { DisconnectDialog } from '@/pages/organization/components/DisconnectDialog';

import {
  BitbucketIntegrationCard,
  type BitbucketCardStatus,
} from './BitbucketIntegrationCard';
import { BitbucketProvider } from './providers';
import {
  useIntegrationSyncHandler,
  useSettledEmptySentryEffect,
} from './useIntegrationSync';

interface BitbucketIntegrationProps {
  access: RenderableIntegrationAccess;
}

export function BitbucketIntegration({
  access,
}: BitbucketIntegrationProps): ReactElement {
  if (access.canRender) return <BitbucketIntegrationInner access={access} />;
  return (
    <BitbucketIntegrationCard
      status={{
        enabled: false,
        connectionStatus: 'none',
        connected: false,
      }}
      cardStatus="not_connected"
      isLoading
    />
  );
}

BitbucketIntegration.displayName = 'BitbucketIntegration';

function BitbucketIntegrationInner({
  access,
}: {
  access: GrantedIntegrationAccess;
}): ReactElement | null {
  const { organizationId, isLoading: isAccessLoading } = access;

  const bitbucketQuery = useBitbucketStatus(organizationId);
  const bitbucketSyncQuery = useBitbucketSyncStatus(organizationId, {
    enabled: bitbucketQuery.data?.connected === true,
  });
  const connectMutation = useBitbucketConnect(organizationId);
  const disconnectMutation = useBitbucketDisconnect(organizationId);
  const updateTokenMutation = useBitbucketUpdateToken(organizationId);
  const syncMutation = useBitbucketSync(organizationId);
  const { toast } = useToast();

  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [updateTokenOpen, setUpdateTokenOpen] = useState(false);
  const [connectError, setConnectError] = useState<string | undefined>();
  const [updateTokenError, setUpdateTokenError] = useState<
    string | undefined
  >();

  const handleConnect = (): void => {
    setConnectError(undefined);
    setConnectOpen(true);
  };

  const handleConnectSubmit = async (data: {
    accessToken: string;
    tokenExpiresAt: string | null;
  }): Promise<void> => {
    setConnectError(undefined);
    try {
      const result = await connectMutation.mutateAsync(data);
      if ('enabled' in result && result.enabled === false) {
        setConnectError('Bitbucket integration is not currently enabled.');
        return;
      }
      if ('success' in result && result.success) {
        setConnectOpen(false);
        toast({
          title: 'Bitbucket connected',
          description: `Successfully connected to ${result.workspaceName}. Syncing workspace members…`,
        });
        return;
      }
      setConnectError('Unexpected response from server. Please try again.');
    } catch (err) {
      Sentry.captureException(err, {
        tags: { error_type: 'bitbucket_connect' },
        extra: { organizationId },
      });
      setConnectError(getErrorMessage(err));
    }
  };

  const handleDisconnect = (): void => setDisconnectOpen(true);
  const closeDisconnect = (): void => setDisconnectOpen(false);

  const handleConfirmDisconnect = async (): Promise<void> => {
    try {
      await disconnectMutation.mutateAsync();
      setDisconnectOpen(false);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { error_type: 'bitbucket_disconnect' },
        extra: { organizationId },
      });
      toast({
        title: 'Disconnect failed',
        description: 'Failed to disconnect Bitbucket. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateToken = (): void => {
    setUpdateTokenError(undefined);
    setUpdateTokenOpen(true);
  };

  const handleUpdateTokenSubmit = async (data: {
    accessToken: string;
    tokenExpiresAt: string | null;
  }): Promise<void> => {
    setUpdateTokenError(undefined);
    try {
      const result = await updateTokenMutation.mutateAsync(data);
      if ('enabled' in result && result.enabled === false) {
        setUpdateTokenError('Bitbucket integration is not currently enabled.');
        return;
      }
      if ('success' in result && result.success) {
        setUpdateTokenOpen(false);
        toast({
          title: 'Token updated',
          description: `Successfully updated the access token for ${result.workspaceName}.`,
        });
        return;
      }
      setUpdateTokenError('Unexpected response from server. Please try again.');
    } catch (err) {
      Sentry.captureException(err, {
        tags: { error_type: 'bitbucket_update_token' },
        extra: { organizationId },
      });
      setUpdateTokenError(getErrorMessage(err));
    }
  };

  const handleSync = useIntegrationSyncHandler({
    provider: BitbucketProvider,
    organizationId,
    syncMutation,
  });

  const cardStatus: BitbucketCardStatus = bitbucketQuery.data?.connected
    ? 'connected'
    : bitbucketQuery.error
      ? 'error'
      : 'not_connected';

  const errorMessage = bitbucketQuery.error
    ? bitbucketQuery.error instanceof Error
      ? bitbucketQuery.error.message
      : 'An unexpected error occurred. Please try again.'
    : undefined;

  const isLoading = isAccessLoading || bitbucketQuery.isLoading;
  const isSyncing =
    syncMutation.isPending || bitbucketSyncQuery.data?.isRunning === true;

  const isSettledEmpty =
    !bitbucketQuery.data &&
    !bitbucketQuery.error &&
    !isAccessLoading &&
    !bitbucketQuery.isLoading;

  useSettledEmptySentryEffect({
    isSettledEmpty,
    provider: BitbucketProvider,
    organizationId,
  });

  if (isSettledEmpty) return null;

  const status = bitbucketQuery.data ?? {
    enabled: false,
    connectionStatus: 'none' as const,
    connected: false,
  };

  return (
    <>
      <BitbucketIntegrationCard
        status={status}
        syncStatus={bitbucketSyncQuery.data}
        cardStatus={cardStatus}
        error={errorMessage}
        isLoading={isLoading}
        isSyncing={isSyncing}
        onConnect={handleConnect}
        onManage={handleUpdateToken}
        onDisconnect={handleDisconnect}
        onSync={access.canWrite ? handleSync : undefined}
        onUpdateToken={handleUpdateToken}
      />
      {connectOpen && (
        <BitbucketConnectDialog
          key={organizationId}
          provider={BitbucketProvider}
          organizationId={organizationId}
          isOpen={connectOpen}
          onClose={() => setConnectOpen(false)}
          onConfirm={handleConnectSubmit}
          isLoading={connectMutation.isPending}
          error={connectError}
          readOnly={!access.canWrite}
        />
      )}
      {updateTokenOpen && (
        <BitbucketUpdateTokenDialog
          key={organizationId}
          organizationId={organizationId}
          workspaceName={bitbucketQuery.data?.workspaceName ?? undefined}
          isOpen={updateTokenOpen}
          onClose={() => setUpdateTokenOpen(false)}
          onConfirm={handleUpdateTokenSubmit}
          isLoading={updateTokenMutation.isPending}
          error={updateTokenError}
          readOnly={!access.canWrite}
        />
      )}
      {disconnectOpen && (
        <DisconnectDialog
          provider={BitbucketProvider}
          isOpen={disconnectOpen}
          onClose={closeDisconnect}
          onConfirm={handleConfirmDisconnect}
          isLoading={disconnectMutation.isPending}
          readOnly={!access.canWrite}
        />
      )}
    </>
  );
}

BitbucketIntegrationInner.displayName = 'BitbucketIntegrationInner';
